/**
 * Type definitions, enums, and utility functions for the Logger SDK.
 */

export const SyslogLevel = {
  emerg: 0,
  alert: 1,
  crit: 2,
  error: 3,
  warning: 4,
  notice: 5,
  info: 6,
  debug: 7,
} as const

export type SyslogLevel = typeof SyslogLevel[keyof typeof SyslogLevel]

export const LogModule = {
  Farmgrid: 0,
  Vault: 1,
} as const

export type LogModule = typeof LogModule[keyof typeof LogModule]

export const LogComponent = {
  frontend: 0,
  backend: 1,
  logger: 2,
  ApiGateway: 3,
} as const

export type LogComponent = typeof LogComponent[keyof typeof LogComponent]

export interface LogEntry {
  logLevel: SyslogLevel
  ts: number
  module: LogModule | number | string
  component: LogComponent | number | string
  commonInfo: Record<string, unknown>
  message: string
  data?: unknown
}

export interface OtelBatchConfig {
  scheduledDelayMillis: number
  maxExportBatchSize: number
  maxQueueSize: number
  exportTimeoutMillis: number
}

export interface OtelConfig {
  enabled: boolean
  serviceName: string
  serviceVersion: string
  environment: string
  collectorUrl: string
  headers?: Record<string, string>
  propagators?: string[]
  batch?: Partial<OtelBatchConfig>
}

export interface LoggerClientConfig {
  module: LogModule | number | string
  component: LogComponent | number | string
  logLevel: SyslogLevel
  commonInfoKeys?: string[]
  consoleStructured?: boolean
  otel?: OtelConfig
}

/**
 * Maps numeric syslog level to its string representation.
 */
export function syslogLevelToString(level: SyslogLevel): string {
  if (level === SyslogLevel.warning) return 'warn'
  const entry = Object.entries(SyslogLevel).find(([, val]) => val === level)
  return entry ? entry[0] : 'unknown'
}

/**
 * Parses string representation to SyslogLevel enum value.
 * Uses case-insensitive matching and handles 'warn' alias.
 */
export function stringToSyslogLevel(level: string): SyslogLevel {
  const normalized = level.toLowerCase().trim()
  if (normalized === 'warn') {
    return SyslogLevel.warning
  }
  
  const parsed = parseInt(normalized, 10)
  if (!isNaN(parsed) && parsed >= 0 && parsed <= 7) {
    return parsed as SyslogLevel
  }
  
  const enumValue = (SyslogLevel as any)[normalized]
  if (typeof enumValue === 'number') {
    return enumValue as SyslogLevel
  }

  // Default fallback if level can't be parsed
  return SyslogLevel.info
}

/**
 * Returns true if the syslog level is considered high priority.
 * High priority levels are 0 (emerg) through 4 (warning).
 */
export function isHighPriorityLevel(level: SyslogLevel): boolean {
  return level >= 0 && level <= 4
}
